import { NotificationChannel } from './notification.model';
import { ConditionOperator } from './support-ticket.model';

export interface AuditLog {
  id: number;
  userId?: number;
  sessionId?: number;
  action: AuditAction;
  entityType: EntityType;
  entityId?: number;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  changes?: AuditChange[];
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
  severity: AuditSeverity;
  category: AuditCategory;
  description?: string;
  descriptionKey?: string;
  metadata?: Record<string, any>;
  isSuccess: boolean;
  errorMessage?: string;
  errorMessageKey?: string;
  duration?: number;
  resource?: string;
  method?: string;
  statusCode?: number;
}

export enum AuditAction {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  LOGIN = 'login',
  LOGOUT = 'logout',
  LOGIN_FAILED = 'login_failed',
  PASSWORD_CHANGE = 'password_change',
  PASSWORD_RESET = 'password_reset',
  EMAIL_VERIFICATION = 'email_verification',
  TWO_FACTOR_ENABLED = 'two_factor_enabled',
  TWO_FACTOR_DISABLED = 'two_factor_disabled',
  PERMISSION_GRANTED = 'permission_granted',
  PERMISSION_REVOKED = 'permission_revoked',
  ROLE_ASSIGNED = 'role_assigned',
  ROLE_REMOVED = 'role_removed',
  ACCOUNT_LOCKED = 'account_locked',
  ACCOUNT_UNLOCKED = 'account_unlocked',
  ACCOUNT_SUSPENDED = 'account_suspended',
  ACCOUNT_ACTIVATED = 'account_activated',
  EXPORT = 'export',
  IMPORT = 'import',
  BACKUP = 'backup',
  RESTORE = 'restore',
  CONFIGURATION_CHANGE = 'configuration_change',
  SYSTEM_STARTUP = 'system_startup',
  SYSTEM_SHUTDOWN = 'system_shutdown',
  ERROR = 'error',
  SECURITY_VIOLATION = 'security_violation',
  DATA_ACCESS = 'data_access',
  FILE_UPLOAD = 'file_upload',
  FILE_DOWNLOAD = 'file_download',
  FILE_DELETE = 'file_delete',
  API_CALL = 'api_call',
  BULK_OPERATION = 'bulk_operation',
  OTHER = 'other',
}

export enum EntityType {
  USER = 'user',
  ROLE = 'role',
  PERMISSION = 'permission',
  REQUEST = 'request',
  INVOICE = 'invoice',
  ANNOUNCEMENT = 'announcement',
  CLASSIFIED_AD = 'classified_ad',
  BOOKING = 'booking',
  PET_REGISTRY = 'pet_registry',
  VENDOR = 'vendor',
  RESIDENT = 'resident',
  PROPERTY = 'property',
  NOTIFICATION = 'notification',
  SUPPORT_TICKET = 'support_ticket',
  AUDIT_LOG = 'audit_log',
  SETTINGS = 'settings',
  SYSTEM = 'system',
}

export enum AuditSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum AuditCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  DATA_ACCESS = 'data_access',
  DATA_MODIFICATION = 'data_modification',
  SYSTEM_OPERATION = 'system_operation',
  SECURITY = 'security',
  CONFIGURATION = 'configuration',
  BUSINESS_LOGIC = 'business_logic',
  INTEGRATION = 'integration',
  PERFORMANCE = 'performance',
  ERROR = 'error',
  COMPLIANCE = 'compliance',
  OTHER = 'other',
}

export interface AuditChange {
  field: string;
  oldValue: any;
  newValue: any;
  changeType: ChangeType;
}

export enum ChangeType {
  ADDED = 'added',
  MODIFIED = 'modified',
  REMOVED = 'removed',
  UNCHANGED = 'unchanged',
}

export interface AuditLogFilter {
  userId?: number;
  action?: AuditAction;
  entityType?: EntityType;
  entityId?: number;
  severity?: AuditSeverity;
  category?: AuditCategory;
  startDate?: Date;
  endDate?: Date;
  isSuccess?: boolean;
  ipAddress?: string;
  searchTerm?: string;
}

export interface AuditLogSummary {
  totalLogs: number;
  logsByAction: Record<AuditAction, number>;
  logsByEntityType: Record<EntityType, number>;
  logsBySeverity: Record<AuditSeverity, number>;
  logsByCategory: Record<AuditCategory, number>;
  logsByUser: Record<number, number>;
  logsByDate: Record<string, number>;
  successRate: number;
  averageDuration: number;
  topUsers: Array<{ userId: number; count: number; userName?: string }>;
  topActions: Array<{ action: AuditAction; count: number }>;
  topEntities: Array<{ entityType: EntityType; count: number }>;
}

export interface AuditLogRetention {
  id: number;
  entityType: EntityType;
  retentionDays: number;
  isActive: boolean;
  createdDate: Date;
  updatedDate: Date;
}

export interface AuditLogExport {
  id: number;
  requestedBy: number;
  filter: AuditLogFilter;
  format: ExportFormat;
  status: ExportStatus;
  filePath?: string;
  fileSize?: number;
  recordCount?: number;
  createdDate: Date;
  completedDate?: Date;
  expiresDate: Date;
}

export enum ExportFormat {
  CSV = 'csv',
  JSON = 'json',
  XML = 'xml',
  PDF = 'pdf',
  EXCEL = 'excel',
}

export enum ExportStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  EXPIRED = 'expired',
}

export interface AuditLogAlert {
  id: number;
  name: string;
  description?: string;
  conditions: AlertCondition[];
  severity: AuditSeverity;
  isActive: boolean;
  notificationChannels: NotificationChannel[];
  recipients: number[];
  cooldownMinutes: number;
  lastTriggered?: Date;
  triggerCount: number;
  createdDate: Date;
  updatedDate: Date;
}

export interface AlertCondition {
  id: number;
  alertId: number;
  field: string;
  operator: ConditionOperator;
  value: any;
  isActive: boolean;
}
